import React, { useState } from "react";
import { Box, Button, Menu, Text, UnstyledButton } from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import usePermissions from "../../hooks/usePermissions";
import { ClientGroupType } from "../../types/clientGroups";
import MarketingOverview from "./MarketingOverview";
import ClientGroupsList from "../clients/groups/ClientGroupsList";
import { ClientGroupsDrawerSlotContext } from "../clients/groups/drawerSlot";
import sx from "../sales/shared/sales.module.css";

/**
 * Marketing page shell. Mirrors the Sales page furniture (README §6): a header
 * band (title + per-tab action) with a borderless sub-tab bar below, a content
 * pane, and a full-page-height drawer column the Client Groups detail panel
 * portals into (so it spans from the top and shrinks the list left, like Sales).
 * A "Campaigns" tab (the existing marketing overview) plus a "Client Groups"
 * tab, gated on the `client_groups` feature (dev-only until rollout); when off,
 * the page falls back to the bare campaigns overview. Active tab URL-synced
 * (?tab=). Header actions: `+ New Campaign` on the campaigns tab, and
 * `+ New Client Group ▾` on the groups tab.
 */
export default function MarketingPageShell() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasFeature } = useFeatureAccess();
  const groupsEnabled = hasFeature("client_groups");
  const perms = usePermissions();
  const canCreateCampaign = perms?.marketing?.canCreate !== false;

  const [newType, setNewType] = useState<ClientGroupType | null>(null);
  // Full-page-height right column the groups list portals its detail panel into,
  // so it spans from the top (beside the header) and shrinks the main column.
  const [drawerSlot, setDrawerSlot] = useState<HTMLDivElement | null>(null);

  const active =
    groupsEnabled && searchParams.get("tab") === "groups"
      ? "groups"
      : "campaigns";

  const setTab = (value: "campaigns" | "groups") => {
    if (value === "campaigns") {
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ tab: value }, { replace: true });
    }
  };

  if (!groupsEnabled) {
    return <MarketingOverview />;
  }

  return (
    <ClientGroupsDrawerSlotContext.Provider value={drawerSlot}>
      <Box className={sx.salesRoot}>
        <Box className={sx.salesMain}>
          <Box className={sx.pageHeader}>
            <Box className={sx.pageHeaderRow}>
              <Box component="h1" className={sx.pageTitle} m={0}>
                Marketing
              </Box>
              <Box style={{ flex: 1 }} />
              {active === "campaigns" && canCreateCampaign && (
                <Button
                  color="var(--brand-teal)"
                  radius={8}
                  leftSection={<IconPlus size={14} stroke={2.4} />}
                  onClick={() => navigate("/admin/marketing/new")}
                >
                  New Campaign
                </Button>
              )}
              {active === "groups" && (
                <Menu position="bottom-end" width={268} shadow="md">
                  <Menu.Target>
                    <Button
                      color="var(--brand-teal)"
                      radius={8}
                      leftSection={<IconPlus size={14} stroke={2.4} />}
                      rightSection={<IconChevronDown size={14} />}
                    >
                      New Client Group
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={() => setNewType("dynamic")}>
                      <Text size="sm" fw={500}>
                        Dynamic Group
                      </Text>
                      <Text size="xs" c="dimmed">
                        Determines client list dynamically based on configured
                        rules.
                      </Text>
                    </Menu.Item>
                    <Menu.Item onClick={() => setNewType("static")}>
                      <Text size="sm" fw={500}>
                        Static Group
                      </Text>
                      <Text size="xs" c="dimmed">
                        You pick the clients, or import a CSV.
                      </Text>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Box>
            <Box className={sx.tabBar}>
              <UnstyledButton
                className={`${sx.tab} ${active === "campaigns" ? sx.tabActive : ""}`}
                onClick={() => setTab("campaigns")}
              >
                Campaigns
              </UnstyledButton>
              <UnstyledButton
                className={`${sx.tab} ${active === "groups" ? sx.tabActive : ""}`}
                onClick={() => setTab("groups")}
              >
                Client Groups
              </UnstyledButton>
            </Box>
          </Box>

          <Box className={sx.contentPane}>
            {active === "groups" ? (
              <ClientGroupsList
                requestNewType={newType}
                onNewTypeConsumed={() => setNewType(null)}
              />
            ) : (
              <MarketingOverview embedded />
            )}
          </Box>
        </Box>

        {/* Full-page-height drawer column; the groups panel portals in here. */}
        <Box ref={setDrawerSlot} className={sx.drawerColumn} />
      </Box>
    </ClientGroupsDrawerSlotContext.Provider>
  );
}
